// `@` 자동완성. 어디를 갈아 끼울지는 순수 함수가 정하고, 메뉴(`mention_list.tsx`)는 그리기만 한다.

export interface Candidate {
  /** 사람 번호 또는 `channel`·`here`. 화면은 키로만 쓴다. */
  id: string | number
  /** 넣을 이름. 공백·`@` 가 없어야 `mentionize` 가 다시 읽는다. */
  name: string
  hint?: string
  /** 이모지 후보의 그림(`:` 메뉴). 커스텀 이모지는 없다. */
  glyph?: string
  /** 커스텀 이모지의 그림 주소 — 웃는 얼굴 표시로 두면 무엇을 고르는지 모른다(3차 F211). */
  src?: string
}

export interface MentionQuery {
  /** `@` 의 자리. */
  start: number
  /** `@` 뒤부터 커서까지. */
  prefix: string
  caret: number
}

/**
 * 커서 바로 앞이 `@앞글자` 인가. `@` 앞은 줄 처음이나 공백이어야 한다 —
 * 이메일 주소 속 `@` 에 메뉴가 뜨면 성가시다.
 */
export function mention_query(text: string, caret: number): MentionQuery | null {
  if (caret < 0 || caret > text.length) return null
  const before = text.slice(0, caret)
  const m = /(?:^|\s)@([^\s@]*)$/.exec(before)
  if (m === null) return null
  const prefix = m[1] ?? ''
  return { start: caret - prefix.length - 1, prefix, caret }
}

/** `@앞글자` 를 `@이름 ` 으로 바꾼 글과 새 커서 자리. */
/**
 * 커맨드는 **글의 맨 앞에서만** 뜬다. 문장 중간의 `/` 는 날짜나 주소다.
 * 뒤에 공백이 오면 이름이 끝난 것이라 메뉴도 닫는다.
 */
export function slash_query(text: string, caret: number): MentionQuery | null {
  if (caret < 0 || caret > text.length) return null
  const m = /^\/([^\s/]*)$/.exec(text.slice(0, caret))
  if (m === null) return null
  const prefix = m[1] ?? ''
  return { start: 0, prefix, caret }
}

/**
 * 커서 바로 앞이 `:앞글자`(두 글자 이상)인가(3차 F136). 슬랙과 같다 — 한 글자면 `10:3` 같은 시각에도 뜬다.
 * `:` 앞은 줄 처음이나 공백이어야 한다(주소 속 `:` 는 이모지가 아니다).
 */
export function emoji_query(text: string, caret: number): MentionQuery | null {
  if (caret < 0 || caret > text.length) return null
  const m = /(?:^|\s):([\w+-]{2,32})$/.exec(text.slice(0, caret))
  if (m === null) return null
  const prefix = (m[1] ?? '').toLowerCase()
  return { start: caret - prefix.length - 1, prefix, caret }
}

/** 글 맨 앞의 `/이름 나머지`. 커맨드가 아니면 `null`. */
export function slash_of(text: string): { name: string; text: string } | null {
  const m = /^\/([^\s/]+)\s*([\s\S]*)$/.exec(text)
  return m === null ? null : { name: m[1] as string, text: m[2] ?? '' }
}

export function apply_mention(
  text: string,
  q: MentionQuery,
  name: string,
  sigil: '@' | '/' | ':' = '@',
): { text: string; caret: number } {
  // 이모지는 `:이름:` 으로 닫는다 — 보낼 때 `body_of` 가 그 모양만 이모지 조각으로 뗀다.
  const head = `${text.slice(0, q.start)}${sigil}${name}${sigil === ':' ? ':' : ''} `
  return { text: head + text.slice(q.caret), caret: head.length }
}
